import { useState, useRef, useEffect } from 'react';
import {
  MdClose,
  MdPictureAsPdf,
  MdDescription,
  MdCheckCircle,
  MdContentPaste,
  MdExpandMore,
  MdExpandLess,
  MdSchool,
  MdMenuBook,
  MdAutoAwesome,
  MdLayers,
  MdCheck,
  MdEdit,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { 
  splitTopicLessons, 
  fileToBase64, 
  extractTextFromWordInBrowser, 
  extractTextFromPdfInBrowser 
} from '../../services/aiService';
import { doc, collection, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getSubjectName, getSubjectClass } from '../../utils/constants';

const AITopicLessonSplitterModal = ({
  isOpen,
  onClose,
  topicId = '',
  topicTitle = '',
  selectedClass = 'SS1',
  selectedSubject = 'mathematics',
  term = 'Term 1',
  userProfile = {},
  isAdmin = false,
  onComplete = () => {},
}) => {
  const [inputMode, setInputMode] = useState('file'); // 'file' or 'text'
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState('');

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Extracted Result State
  const [extractedLessons, setExtractedLessons] = useState([]);
  const [selectedLessonIndices, setSelectedLessonIndices] = useState(new Set());
  const [expandedLessonIndex, setExpandedLessonIndex] = useState(0);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccessMsg, setPublishSuccessMsg] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setPastedText('');
      setExtractedLessons([]);
      setSelectedLessonIndices(new Set());
      setErrorMsg('');
      setPublishSuccessMsg('');
    }
  }, [isOpen, topicId]);

  if (!isOpen) return null;

  const fileToText = (fileObj) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(fileObj);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleAnalyze = async () => {
    setErrorMsg('');
    setPublishSuccessMsg('');

    if (inputMode === 'file' && !file) {
      setErrorMsg('Please select a PDF or Text document to import.');
      return;
    }
    if (inputMode === 'text' && !pastedText.trim()) {
      setErrorMsg('Please paste your multi-lesson notes in the box below.');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisStep('Reading document content...');

    try {
      let textPayload = '';
      let base64PdfPayload = null;

      if (inputMode === 'file' && file) {
        const fileName = (file.name || '').toLowerCase();
        const isPdf = file.type === 'application/pdf' || fileName.endsWith('.pdf');
        const isWord = fileName.endsWith('.doc') || fileName.endsWith('.docx') || file.type.includes('word') || file.type.includes('officedocument');

        if (isWord) {
          setAnalysisStep('Extracting Word document text...');
          const docResult = await extractTextFromWordInBrowser(file);
          textPayload = docResult.text || '';
        } else if (isPdf) {
          setAnalysisStep('Parsing PDF pages & formatting...');
          try {
            const pdfResult = await extractTextFromPdfInBrowser(file);
            if (pdfResult && pdfResult.text && pdfResult.text.length > 50) {
              textPayload = pdfResult.text;
            } else {
              base64PdfPayload = await fileToBase64(file);
            }
          } catch (_) {
            base64PdfPayload = await fileToBase64(file);
          }
        } else {
          setAnalysisStep('Reading text content...');
          textPayload = await fileToText(file);
        }
      } else {
        textPayload = pastedText.trim();
      }

      if (!textPayload.trim() && !base64PdfPayload) {
        throw new Error('No readable text could be extracted from this file. Please copy and paste your notes directly into the "Paste Notes" tab.');
      }

      setAnalysisStep(`AI is scanning & splitting lessons for "${topicTitle}"...`);

      const result = await splitTopicLessons({
        text: textPayload,
        base64Pdf: base64PdfPayload,
        topicTitle: topicTitle || 'Curriculum Topic',
        classLevel: selectedClass,
        subject: getSubjectName(selectedSubject) || selectedSubject,
        term: term || 'Term 1',
      });

      if (result?.error) {
        throw new Error(result.error);
      }

      let rawLessons = [];
      if (Array.isArray(result)) {
        rawLessons = result;
      } else if (result?.lessons && Array.isArray(result.lessons)) {
        rawLessons = result.lessons;
      } else if (result?.topics && Array.isArray(result.topics)) {
        rawLessons = result.topics;
      } else if (result?.data && Array.isArray(result.data)) {
        rawLessons = result.data;
      }

      // If AI couldn't find sub-headings, fallback to a structured single lesson so the teacher is never blocked!
      if (!rawLessons || !Array.isArray(rawLessons) || rawLessons.length === 0) {
        rawLessons = [{
          lessonNumber: 1,
          title: topicTitle || 'Introduction & Core Concepts',
          subtopic: 'Fundamental Principles',
          learningObjectives: [`Master the core fundamentals of ${topicTitle}`],
          lessonNotes: textPayload ? textPayload.slice(0, 5000) : `Comprehensive lesson on ${topicTitle}.`,
          analogies: `Real-life practical application of ${topicTitle}.`,
          keyRule: `Key governing formula and principle for ${topicTitle}.`,
          practiceQuestions: []
        }];
      }

      setExtractedLessons(rawLessons);
      // Select all detected lessons by default
      setSelectedLessonIndices(new Set(rawLessons.map((_, i) => i)));
      setExpandedLessonIndex(0);
    } catch (err) {
      console.error('Lesson split error:', err);
      setErrorMsg(err.message || 'Failed to analyze document. Please verify your connection.');
    } finally {
      setIsAnalyzing(false);
      setAnalysisStep('');
    }
  };

  const toggleLessonSelect = (index) => {
    const next = new Set(selectedLessonIndices);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setSelectedLessonIndices(next);
  };

  const updateLessonField = (index, field, value) => {
    setExtractedLessons((prev) => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], [field]: value };
      }
      return copy;
    });
  };

  const handlePublishAll = async () => {
    if (extractedLessons.length === 0 || selectedLessonIndices.size === 0) {
      setErrorMsg('Please select at least one lesson to publish.');
      return;
    }

    setIsPublishing(true);
    setErrorMsg('');
    setPublishSuccessMsg('');

    try {
      const teacherUid = userProfile?.uid || 'admin_user';
      const teacherName = userProfile?.fullName || (isAdmin ? 'Admin' : 'Teacher');
      const targetSub = selectedSubject || 'mathematics';
      const targetSubName = getSubjectName(targetSub);
      const targetClass = selectedClass || 'SS1';
      const effectiveTerm = term || 'Term 1';

      let publishedCount = 0;
      let questionCount = 0;
      const allSubtopicTitles = [];

      for (const idx of selectedLessonIndices) {
        const lesson = extractedLessons[idx];
        if (!lesson) continue;

        const cleanLessonTitle = (lesson.title || `Lesson ${idx + 1}`).trim();
        const cleanSubtopic = (lesson.subtopic || cleanLessonTitle).trim();
        if (cleanSubtopic && !allSubtopicTitles.includes(cleanSubtopic)) {
          allSubtopicTitles.push(cleanSubtopic);
        }

        const lessonRef = doc(collection(db, 'content_approvals'));
        const lessonBody = lesson.lessonNotes || '';
        const fullBody = lesson.analogies
          ? `${lessonBody}\n\n### Everyday Analogy\n${lesson.analogies}`
          : lessonBody;

        await setDoc(lessonRef, {
          id: lessonRef.id,
          title: cleanLessonTitle,
          type: 'Lesson',
          subjectId: targetSub,
          subjectName: targetSubName,
          classLevel: targetClass,
          teacherId: teacherUid,
          teacherName,
          status: 'approved',
          createdAt: serverTimestamp(),
          contentData: {
            topicId,
            topicIds: topicId ? [topicId] : [],
            topicName: topicTitle,
            subtopic: cleanSubtopic,
            body: fullBody,
            contentType: 'text',
            learningObjectives: lesson.learningObjectives || [],
            keyRule: lesson.keyRule || '',
            subtopics: [cleanSubtopic],
            term: effectiveTerm,
          },
        });
        publishedCount++;

        // Save review practice questions if generated
        if (lesson.practiceQuestions && Array.isArray(lesson.practiceQuestions)) {
          for (const q of lesson.practiceQuestions) {
            if (!q.question) continue;
            const qRef = doc(collection(db, 'curriculum_questions'));
            const opts = Array.isArray(q.options) && q.options.length >= 2 ? q.options : ['A', 'B', 'C', 'D'];
            const correct =
              q.correctAnswer ||
              (typeof q.correctOptionIndex === 'number' && opts[q.correctOptionIndex] ? opts[q.correctOptionIndex] : opts[0]);

            await setDoc(qRef, {
              id: qRef.id,
              topicId: topicId || `topic_${Date.now()}`,
              topicTitle,
              subjectId: targetSub,
              classId: targetClass.toLowerCase(),
              difficulty: 2,
              question: q.question,
              questionText: q.question,
              options: opts,
              correctAnswer: correct,
              explanation: q.explanation || 'Verified curriculum standard answer.',
              status: 'approved',
              createdAt: serverTimestamp(),
            });
            questionCount++;
          }
        }
      }

      // Update the parent Topic in content_approvals and curriculum_topics with new subtopics
      if (topicId && allSubtopicTitles.length > 0) {
        try {
          const topicDocRef = doc(db, 'content_approvals', topicId);
          await updateDoc(topicDocRef, {
            subtopics: allSubtopicTitles,
            updatedAt: serverTimestamp(),
          });
        } catch (_) {}

        try {
          const currDocRef = doc(db, 'curriculum_topics', topicId);
          await updateDoc(currDocRef, {
            subtopics: allSubtopicTitles,
            updatedAt: serverTimestamp(),
          });
        } catch (_) {}
      }

      setPublishSuccessMsg(
        `🎉 Successfully published ${publishedCount} distinct lessons and ${questionCount} quiz questions under "${topicTitle}"!`
      );
      setTimeout(() => {
        onComplete();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Publishing error:', err);
      setErrorMsg(err.message || 'Failed to publish lessons to Firebase.');
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center ${isFullScreen ? 'p-0' : 'p-3 sm:p-4'} animate-fadeIn`}>
      <div className={`bg-white shadow-2xl flex flex-col overflow-hidden border border-gray-100 transition-all duration-200 ${
        isFullScreen ? 'w-screen h-screen rounded-none' : 'w-full max-w-5xl max-h-[92vh] rounded-3xl'
      }`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 text-white p-5 sm:p-6 shrink-0 relative">
          <div className="absolute top-5 right-5 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              title={isFullScreen ? 'Restore Down' : 'Full Screen'}
            >
              {isFullScreen ? <MdFullscreenExit size={22} /> : <MdFullscreen size={22} />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              title="Close"
            >
              <MdClose size={22} />
            </button>
          </div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="p-2 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-indigo-950 shadow-md">
              <MdAutoAwesome size={20} />
            </span>
            <div>
              <h2 className="text-xl sm:text-2xl font-black font-poppins tracking-tight">
                AI Multi-Lesson Document Splitter
              </h2>
              <p className="text-xs sm:text-sm text-indigo-200">
                Upload one document covering multiple sub-lessons under this topic — AI splits them automatically!
              </p>
            </div>
          </div>

          {/* Current Topic Context Badge */}
          <div className="mt-3 inline-flex flex-wrap items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-xl text-xs font-semibold">
            <span className="text-amber-300 flex items-center gap-1">
              <MdLayers size={15} /> Topic:
            </span>
            <span className="text-white font-bold">{topicTitle || 'Selected Topic'}</span>
            <span className="text-white/40">•</span>
            <span className="text-indigo-200">{selectedClass}</span>
            <span className="text-white/40">•</span>
            <span className="text-indigo-200">{getSubjectName(selectedSubject)}</span>
            <span className="text-white/40">•</span>
            <span className="text-indigo-200">{term}</span>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {errorMsg && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0"></span>
              <span>{errorMsg}</span>
            </div>
          )}

          {publishSuccessMsg && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl text-sm font-bold flex items-center gap-3 shadow-xs">
              <MdCheckCircle className="text-emerald-600 text-xl shrink-0" />
              <span>{publishSuccessMsg}</span>
            </div>
          )}

          {/* If analysis is not yet done: Show Input Area */}
          {extractedLessons.length === 0 ? (
            <div className="space-y-5 max-w-4xl mx-auto w-full py-2">
              {/* Input Mode Selector */}
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-2xl max-w-sm">
                <button
                  type="button"
                  onClick={() => setInputMode('file')}
                  className={`flex-1 py-2 px-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                    inputMode === 'file'
                      ? 'bg-white text-indigo-700 shadow-xs'
                      : 'text-gray-600 hover:text-darkNavy'
                  }`}
                >
                  <MdPictureAsPdf size={16} /> Upload PDF / Word
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('text')}
                  className={`flex-1 py-2 px-3 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                    inputMode === 'text'
                      ? 'bg-white text-indigo-700 shadow-xs'
                      : 'text-gray-600 hover:text-darkNavy'
                  }`}
                >
                  <MdContentPaste size={16} /> Paste Notes
                </button>
              </div>

              {/* Mode 1: File Dropzone */}
              {inputMode === 'file' && (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-3xl p-8 sm:p-10 text-center cursor-pointer transition-all ${
                    file
                      ? 'border-indigo-500 bg-indigo-50/50'
                      : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50/70'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt,.text,.md,.doc,.docx"
                    onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                    className="hidden"
                  />
                  {file ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-md">
                        <MdPictureAsPdf size={32} />
                      </div>
                      <p className="text-base font-bold text-darkNavy">{file.name}</p>
                      <p className="text-xs text-gray-500 font-medium">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB • Ready to analyze
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="text-xs text-red-600 hover:underline font-bold mt-1"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
                        <MdDescription size={32} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-darkNavy">
                          Drop your multi-lesson document here, or <span className="text-indigo-600 underline">browse</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Supports .PDF, .TXT, Word documents containing multiple lessons under &quot;{topicTitle}&quot;
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mode 2: Paste Text */}
              {inputMode === 'text' && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                    Paste Lecture Notes (All sub-lessons):
                  </label>
                  <textarea
                    rows={12}
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste the full lecture notes containing Lesson 1, Lesson 2, Lesson 3, worked examples, and questions..."
                    className="w-full p-4 bg-gray-50 border border-gray-300 rounded-2xl text-xs sm:text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all"
                  />
                </div>
              )}

              {/* Analyze Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  className="w-full py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  {isAnalyzing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{analysisStep || 'Analyzing Document...'}</span>
                    </>
                  ) : (
                    <>
                      <MdAutoAwesome className="text-amber-300 text-lg" />
                      <span>✨ Analyze & Split Lessons with AI</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Results & Preview Area */
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-indigo-50/70 border border-indigo-200 rounded-2xl">
                <div>
                  <h3 className="text-base font-bold text-indigo-950 flex items-center gap-2">
                    <MdCheckCircle className="text-emerald text-lg" style={{ color: '#16a34a' }} />
                    <span>AI Detected {extractedLessons.length} Distinct Lessons</span>
                  </h3>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    Review and customize each lesson before publishing under &quot;{topicTitle}&quot;.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedLessonIndices.size === extractedLessons.length) {
                        setSelectedLessonIndices(new Set());
                      } else {
                        setSelectedLessonIndices(new Set(extractedLessons.map((_, i) => i)));
                      }
                    }}
                    className="text-xs font-bold text-indigo-700 hover:underline px-2 py-1"
                  >
                    {selectedLessonIndices.size === extractedLessons.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExtractedLessons([]);
                      setSelectedLessonIndices(new Set());
                    }}
                    className="text-xs font-bold text-gray-500 hover:text-red-600 px-2 py-1"
                  >
                    Re-upload
                  </button>
                </div>
              </div>

              {/* Lesson Cards Accordion */}
              <div className="space-y-3">
                {extractedLessons.map((lesson, idx) => {
                  const isSelected = selectedLessonIndices.has(idx);
                  const isExpanded = expandedLessonIndex === idx;

                  return (
                    <div
                      key={idx}
                      className={`border rounded-2xl transition-all overflow-hidden ${
                        isSelected
                          ? 'border-indigo-300 bg-white shadow-xs'
                          : 'border-gray-200 bg-gray-50/60 opacity-60'
                      }`}
                    >
                      {/* Card Header */}
                      <div className="p-4 flex items-center justify-between gap-3 cursor-pointer select-none">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleLessonSelect(idx)}
                            className="w-5 h-5 rounded-md text-indigo-600 focus:ring-indigo-500 border-gray-300 cursor-pointer shrink-0"
                          />
                          <span className="px-2.5 py-1 rounded-lg bg-indigo-100 text-indigo-700 font-black text-xs flex items-center justify-center shrink-0 tracking-wide whitespace-nowrap">
                            Lesson {lesson.lessonNumber || idx + 1}
                          </span>
                          <div className="flex-1 min-w-0 truncate" onClick={() => setExpandedLessonIndex(isExpanded ? -1 : idx)}>
                            <p className="text-sm font-bold text-darkNavy truncate">
                              {lesson.title || `Lesson ${idx + 1}`}
                            </p>
                            <p className="text-xs text-indigo-600 truncate">
                              Subtopic: {lesson.subtopic || 'Core Concept'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {lesson.practiceQuestions && lesson.practiceQuestions.length > 0 && (
                            <span className="hidden sm:inline-block text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md">
                              {lesson.practiceQuestions.length} Quiz Qs
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setExpandedLessonIndex(isExpanded ? -1 : idx)}
                            className="p-1.5 text-gray-400 hover:text-darkNavy rounded-lg hover:bg-gray-100"
                          >
                            {isExpanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 pt-0 border-t border-gray-100 space-y-4 bg-gray-50/40">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1">Lesson Title</label>
                              <input
                                type="text"
                                value={lesson.title || ''}
                                onChange={(e) => updateLessonField(idx, 'title', e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-indigo-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1">Subtopic / Concept</label>
                              <input
                                type="text"
                                value={lesson.subtopic || ''}
                                onChange={(e) => updateLessonField(idx, 'subtopic', e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-indigo-500 outline-none"
                              />
                            </div>
                          </div>

                          {/* Objectives preview */}
                          {lesson.learningObjectives && lesson.learningObjectives.length > 0 && (
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1">Learning Objectives</label>
                              <ul className="list-disc list-inside text-xs text-gray-600 bg-white p-3 rounded-xl border border-gray-200 space-y-0.5">
                                {lesson.learningObjectives.map((obj, oIdx) => (
                                  <li key={oIdx}>{obj}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Notes preview */}
                          <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Lesson Notes Content</label>
                            <textarea
                              rows={4}
                              value={lesson.lessonNotes || ''}
                              onChange={(e) => updateLessonField(idx, 'lessonNotes', e.target.value)}
                              className="w-full p-3 bg-white border border-gray-300 rounded-xl text-xs font-mono focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                          </div>

                          {/* Analogy & Key rule preview */}
                          {(lesson.analogies || lesson.keyRule) && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                              {lesson.analogies && (
                                <div className="p-2.5 bg-blue-50/70 border border-blue-200 rounded-xl text-blue-900">
                                  <strong>Everyday Analogy:</strong> {lesson.analogies}
                                </div>
                              )}
                              {lesson.keyRule && (
                                <div className="p-2.5 bg-amber-50/70 border border-amber-200 rounded-xl text-amber-900">
                                  <strong>Key Rule / Formula:</strong> {lesson.keyRule}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer - Docked & Always Visible */}
        {extractedLessons.length > 0 && (
          <div className="p-4 px-6 sm:px-8 border-t border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm font-bold text-gray-800">
                {selectedLessonIndices.size} of {extractedLessons.length} lessons selected
              </span>
              <span className="text-xs text-gray-400 hidden sm:inline">•</span>
              <span className="text-xs text-indigo-700 font-semibold hidden sm:inline">
                Under &quot;{topicTitle}&quot;
              </span>
            </div>

            <button
              type="button"
              onClick={handlePublishAll}
              disabled={isPublishing || selectedLessonIndices.size === 0}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 60%, #047857 100%)',
                boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.4)',
              }}
              className="px-7 py-3 text-white font-bold text-sm rounded-xl transition-all flex items-center gap-2.5 cursor-pointer hover:brightness-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-400/30"
            >
              {isPublishing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="font-semibold text-white">Saving to Firebase...</span>
                </>
              ) : (
                <>
                  <MdCheck size={20} className="text-white shrink-0" />
                  <span className="font-bold text-white tracking-wide">
                    Save ({selectedLessonIndices.size}) Lessons to Firebase
                  </span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITopicLessonSplitterModal;
